/**
 * A document open beside a session is a document that session has in hand
 * (RFC 0297 §4b, the `open` row).
 *
 * Putting a document on screen next to the conversation is the physical form of
 * handing it over, so it PINS: unlike a read, it never falls out of the list.
 *
 * AN OPEN IS A WINDOW ACT AND A LOOK IS A SESSION ACT, which is the whole
 * difficulty. The window knows what is on screen; the log knows what a session
 * holds. The space is what joins them: a document leaf and an agent leaf in one
 * space means that session can see that document, and a space with no agent leaf
 * hands it to nobody and records nothing.
 *
 * ONCE PER OPEN, NOT PER RENDER, and this is the part that would be silently
 * catastrophic. A touch moves the watermark, so a touch appended on every render
 * would keep `sawUpdatedAt` pinned to now and NOTHING would ever read as
 * changed. The bar would look alive and report nothing, which is the exact
 * failure the RFC exists to prevent. So the pairs already recorded are
 * remembered, and only a pair that was not on screen before is written.
 */

import type { Repo } from '@automerge/automerge-repo'
import { pairOnOpen, recordOpenTouch } from '@mod/core/model'
import { useEffect, useRef } from 'react'
import {
  type ContentLeaf,
  type ContentNode,
  leaves,
} from '../../features/code/contentTree'

/**
 * What a document pane holds, which is not always the document it opened.
 *
 * AN APP PANE HOLDS WHAT IT SHOWS. Opening an app by name opens its VIEW
 * document, which is app source, while the thing on screen is a record the app
 * chose and named with `surface.setSubject`. Holding the view put the Review
 * app's `index.html` in every session's hand and no review in any of them.
 * So `holds` answers per leaf: the declared subject, nothing for an app's view
 * that has not declared one, and the document itself otherwise.
 */
export type PaneHolds = (leaf: ContentLeaf) => string | null

const holdsItself: PaneHolds = (l) =>
  l.content.kind === 'document' ? l.content.docId : null

/** Which session can see which documents, from what is on screen. */
export function openPairs(
  tree: ContentNode | null,
  holds: PaneHolds = holdsItself,
): Array<[string, string]> {
  const all = leaves(tree)
  const sessionIds = all
    .map((l) => (l.content.kind === 'agent' ? l.content.sessionId : null))
    .filter((id): id is string => Boolean(id))
  const docIds = [
    ...new Set(
      all
        .map((l) => (l.content.kind === 'document' ? holds(l) : null))
        .filter((id): id is string => Boolean(id)),
    ),
  ]

  // Every session in the space sees every document in it. A space with two
  // conversations side by side is one project, not two.
  const out: Array<[string, string]> = []
  for (const sessionId of sessionIds) {
    for (const docId of docIds) out.push([sessionId, docId])
  }
  return out
}

export function useOpenTouches(
  repo: Repo,
  tree: ContentNode | null,
  holds: PaneHolds = holdsItself,
): void {
  // Survives re-renders, so a pair is recorded once per window rather than once
  // per paint. Deliberately NOT persisted: reopening a document in a new window
  // is a fresh open and should say so, and the fold treats a repeat as a move of
  // the watermark rather than as a duplicate row.
  const recorded = useRef(new Set<string>())

  useEffect(() => {
    for (const [sessionId, documentId] of openPairs(tree, holds)) {
      const key = `${sessionId} ${documentId}`
      if (recorded.current.has(key)) continue
      recorded.current.add(key)
      // RFC 0301 §3a: side by side is paired. Only within one project, since
      // a pairing lives on that project's project document.
      const projectId = projectOfPair(tree, sessionId, documentId, holds)
      if (projectId) {
        void pairOnOpen(repo as never, projectId, sessionId, documentId).catch(
          () => {},
        )
      }
      void recordOpenTouch(repo, sessionId, documentId).catch(() => {
        // A touch that cannot be written is a holding that is merely absent.
        // Nothing on screen depends on it, so there is nothing to recover.
        recorded.current.delete(key)
      })
    }
  }, [repo, tree, holds])
}

/**
 * The project a session and a document on screen share, or null when they sit
 * in different projects. A pairing is written on one project's project, so a
 * session cannot be paired to a document it does not share a project with.
 */
export function projectOfPair(
  tree: ContentNode | null,
  sessionId: string,
  documentId: string,
  holds: PaneHolds = holdsItself,
): string | null {
  const all = leaves(tree)
  const session = all.find(
    (l) => l.content.kind === 'agent' && l.content.sessionId === sessionId,
  )?.content
  const doc = all.find(
    (l) => l.content.kind === 'document' && holds(l) === documentId,
  )?.content
  const a = session && 'projectId' in session ? session.projectId : null
  const b = doc && 'projectId' in doc ? doc.projectId : null
  return a && a === b ? a : null
}
